
import { Component, Vue, Prop } from 'vue-property-decorator'
import { actionTypesModule, commonModule, installationsModule, itemsModule, offersModule } from '@/store/modules/store-accessor'
import { formatAmount, getOptimalTextColor } from '@/helpers/data'
import { greekSearchFilter } from "@/helpers/data";

@Component
export default class EditOfferLineItemModal extends Vue {
  @Prop()
  payload!: Record<string, any>;

  greekSearchFilter = greekSearchFilter;

  isEditingOfferItem = false;

  lineItemModel = {
    line_item_id: this.payload.data.lineItemId,
    offer_id: this.payload.data.offerId,
    installation_id: this.payload.data.installationId,
    line_type: '',
    quantity: 1,
    unit_cost: 0,
    discount_percentage: 0,
    description: '',
    notes: '',
    sort_order: 0,
    action_type: null as any,
    material: null as any,
    inventory_item_id: null as string | number | null,
    inventory_unit: null as any,
    time_unit: null as any,
  }

  actionRules = [
    (val: any) => (!!val && val.text != "") || this.$i18n.t("action_required"),
  ]

  unitOptionRules = [
    (val: any) => ((!!val || val == '') && val?.text?.length > 0) || this.$i18n.t("unitOptionRule"),
  ]

  quantityRules = [
    (val: any) => Number(val) >= 1 || this.$i18n.t("requiredNonZeroQuantityRule"),
  ]

  unitCostRules = [
    (val: any) => val !== '' && val !== null || this.$i18n.t("priceOfferRequiredRule"),
    (val: any) => Number(val) >= 0 || this.$i18n.t("priceOfferNonNegativedRule"),
  ]

  materialRules = [
    (val: any) => {
      if (!val && val !== 0) return this.$i18n.t("materialFieldRequiredRule");
      if (typeof val === 'string') return val.trim().length > 0 || this.$i18n.t("materialFieldRequiredRule");
      return val.text?.trim().length > 0 || this.$i18n.t("materialFieldRequiredRule");
    }
  ]

  descriptionRules = [
    (val: any) => !!val || this.$i18n.t("descriptionRequiredRule"),
    (val: any) => val.length <= 200 || this.$i18n.t("descriptionLengthRule"),
  ]

  notesRule = [
    (val: any) => !val || val.length <= 500 || this.$i18n.t("offerLineItemNotesLengthRule"),
  ]

  discountRule = [
    (val: any) => val !== '' && val !== null && Number(val) >= 0 && Number(val) <= 100 || this.$i18n.t("discountPercentageRule"),
  ]

  get currentOfferDetails() {
    return installationsModule.installationOfferDetails;
  }

  get currentLineItem(): OfferLineItemDto | undefined {
    return this.currentOfferDetails?.line_items?.find((item: OfferLineItemDto) => item.id === this.payload.data.lineItemId);
  }

  get actionsCombo() {
    return actionTypesModule.actionTypesCombo;
  }

  get inventoryItemsCombo(): any[] {
    return commonModule.inventoryItemsCombo;
  }

  get itemsUnitsTypes(): any[] {
    return itemsModule.itemsUnitsCombo;
  }

  get timeChargedUnitCombo() {
    return commonModule.timeChargedUnitCombo;
  }

  get isActionLine() {
    return this.lineItemModel.line_type === 'ACTION';
  }

  get isMaterialLine() {
    return this.lineItemModel.line_type === 'MATERIAL';
  }

  get isCustomLine() {
    return this.lineItemModel.line_type === 'CUSTOM';
  }

  get actionHasTimeUnit() {
    return !!this.actionDefaultTimeUnit() || !!this.lineItemModel.time_unit;
  }

  get materialHasInventoryUnit() {
    return !!this.lineItemModel.inventory_unit || !!this.lineItemModel.material?.is_unit_based;
  }

  get showQuantityField() {
    return this.isCustomLine || this.actionHasTimeUnit || this.materialHasInventoryUnit;
  }

  async hideModal() {
    commonModule.hideModal();
  }

  async mounted() {
    commonModule.initSnackbar({});
    await Promise.all([
      actionTypesModule.getActionTypesCombo(),
      commonModule.getItemsCombo(),
      commonModule.getTimeChargedUnitCombo(),
      itemsModule.getItemsUnitsCombo(),
    ]);
    this.lineItemIsReady();
  }

  lineItemIsReady() {
    const lineItem = this.currentLineItem;
    if (!lineItem) return;

    this.lineItemModel.line_type = lineItem.line_type;
    this.lineItemModel.quantity = lineItem.quantity;
    this.lineItemModel.unit_cost = lineItem.unit_cost;
    this.lineItemModel.discount_percentage = lineItem.discount_percentage;
    this.lineItemModel.description = lineItem.description;
    this.lineItemModel.notes = lineItem.notes || '';
    this.lineItemModel.sort_order = lineItem.sort_order;
    this.lineItemModel.inventory_item_id = lineItem.inventory_item_id;

    if (this.isActionLine) {
      this.lineItemModel.action_type = this.actionsCombo.find((item: any) => item.id === lineItem.action_type_id) || null;
      this.lineItemModel.time_unit = this.timeUnitOption(lineItem.time_unit);
    }

    if (this.isMaterialLine) {
      this.lineItemModel.material = this.inventoryItemsCombo.find((item: any) => item.inventory_item_id === lineItem.inventory_item_id) || lineItem.description || null;
      this.lineItemModel.inventory_unit = this.inventoryUnitOption(lineItem.inventory_unit);
    }
  }

  onActionTypeChange() {
    if (!this.lineItemModel.action_type) return;

    this.lineItemModel.description = this.lineItemModel.action_type.text || this.lineItemModel.description;
    this.lineItemModel.unit_cost = Number(this.lineItemModel.action_type.cost || this.lineItemModel.unit_cost || 0);
    this.lineItemModel.time_unit = this.timeUnitOption(this.actionDefaultTimeUnit());

    this.lineItemModel.quantity = 1; // Change it back to the default value as when the action does not have time_unit the user can not change the quantity
  }

  actionDefaultTimeUnit() {
    const actionType = this.lineItemModel.action_type;
    const timeUnit = actionType?.time_unit || actionType?.unit?.unit || actionType?.unit;

    if (!actionType?.is_time_charged && !actionType?.time_unit) {
      return null;
    }

    return ['HOUR', 'DAY'].includes(String(timeUnit).toUpperCase()) ? String(timeUnit).toUpperCase() : null;
  }

  timeUnitOption(timeUnit: string | null) {
    if (!timeUnit) return null;
    return this.timeChargedUnitCombo.find((item: any) => item.unit === timeUnit) || null;
  }

  inventoryUnitOption(inventoryUnit: string | null) {
    if (!inventoryUnit) return null;
    return (this.itemsUnitsTypes as any[]).find((item: any) => item.enum_text === inventoryUnit) || null;
  }

  findItemUnit(unitType: Record<string, any>) {
    if (!unitType) return;

    const foundUnitType: any = (this.itemsUnitsTypes as any[]).find((item: any) => item.enum_text == unitType.unit);
    this.lineItemModel.description = unitType.text || unitType;
    this.lineItemModel.inventory_unit = foundUnitType
      ? { text: foundUnitType.text, enum_text: foundUnitType.enum_text, decimals: foundUnitType.decimals }
      : null;
    this.lineItemModel.unit_cost = unitType.cost ?? this.lineItemModel.unit_cost;
    this.lineItemModel.inventory_item_id = unitType.inventory_item_id ?? null;

    this.lineItemModel.quantity = 1; // Change it back to the default value as when the action does not have time_unit the user can not change the quantity
  }
  
  optimalTextColor(color: string) {
    return getOptimalTextColor(color);
  }

  formatBalance(balance: number) {
    return formatAmount(balance);
  }

  get lineTotal() {
    const quantity = Number(this.lineItemModel.quantity || 0);
    const unitCost = Number(this.lineItemModel.unit_cost || 0);
    const discountPercentage = Math.min(Math.max(Number(this.lineItemModel.discount_percentage || 0), 0), 100);
    return quantity * unitCost * (1 - discountPercentage / 100);
  }

  async doUpdateOfferItem() {
    if (!(this.$refs.form as Vue & { validate: () => boolean }).validate()) {
      return;
    }

    try {
      this.isEditingOfferItem = true;
      await offersModule.updateOfferLineItem(this.lineItemModel);
      this.hideModal();
    } finally {
      this.isEditingOfferItem = false;
    }
  }
};
